import React, { useEffect } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

/* ================== Layout ================== */
const Section = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  padding: 100px 5%;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 60px 5%;
    gap: 28px;
  }
`;

const TextBox = styled.div`
  flex: 1;
  padding-right: 10%;

  h2 { font-size: 25px; margin-bottom: 10px; white-space: pre-line; color: #005BAC; }
  h1 { font-size: 50px; margin-bottom: 40px; white-space: pre-line; }
  p  { font-size: 14px; line-height: 1.7; white-space: pre-line; margin-bottom: 20px; }

  @media (max-width: 1024px) { padding-right: 6%; }
  @media (max-width: 768px) {
    padding-right: 0;
    text-align: left;
    h1 { font-size: 34px; margin-bottom: 24px; }
    h2 { font-size: 20px; }
    p  { font-size: 15px; }
  }
`;

/* ===== SliderBox: 모바일 폭 0 계산 방지 + 도트 전역 ul 스타일 격리 ===== */
const SliderBox = styled.div`
  --slide-h-desktop: 420px;
  --slide-h-mobile: 320px;

  flex: 1;
  max-width: 550px;
  position: relative;

  /* ✅ flex 환경에서 slick 초기 폭 0/깨짐 방지 */
  width: 100%;
  min-width: 0;

  .slick-slider,
  .slick-list { width: 100%; }

  /* 슬라이드 영역만 overflow 숨김 */
  .slick-list { overflow: hidden; }

  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div {
    height: var(--slide-h-desktop);
  }

  .slick-slide {
    display: flex !important;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .slick-prev, .slick-next { z-index: 1; }
  .slick-prev:before, .slick-next:before { display: none; }

  /* ✅ 전역 ul/ol/li 모바일 스타일로부터 slick-dots 격리 */
  .slick-dots {
    position: absolute;
    bottom: -28px;   /* 기존처럼 아래로 살짝 내릴 거면 overflow hidden 없으니 OK */
    left: 0;
    right: 0;

    display: flex !important;
    flex-direction: row !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 8px !important;

    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    list-style: none !important;
  }
  .slick-dots li {
    margin: 0 !important;
    width: auto !important;
    height: auto !important;
  }

  @media (max-width: 768px) {
    max-width: 100%;

    .slick-list,
    .slick-track,
    .slick-slide,
    .slick-slide > div {
      height: var(--slide-h-mobile);
    }

    .slick-dots {
      bottom: -22px;
      gap: 6px !important;
    }
  }
`;

const SlideFrame = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

/* ===== Arrows ===== */
const ArrowButton = styled.button`
  display: flex !important;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 40%;
  background: rgba(0,0,0,0.4);
  color: #fff;
  width: 40px; height: 40px;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  &:hover { background: rgba(0,0,0,0.7); }

  @media (max-width: 520px) {
    display: none !important;
  }
`;
const PrevArrow = ({ onClick }) => (
  <ArrowButton onClick={onClick} style={{ left: '-10%' }} aria-label="이전">
    <FaArrowLeft />
  </ArrowButton>
);
const NextArrow = ({ onClick }) => (
  <ArrowButton onClick={onClick} style={{ right: '-10%' }} aria-label="다음">
    <FaArrowRight />
  </ArrowButton>
);

/* ===== Video ===== */
const Video = styled.video`
  max-width: 100%;
  max-height: 70vh;
  width: auto;
  height: auto;
  display: block;
  margin: 0 auto;
`;

/* ===== Badges ===== */
const BadgeWrapper = styled.div`
  display: flex; flex-wrap: wrap; gap: 16px;
  margin-top: 10%;
  @media (max-width: 768px) { margin-top: 24px; gap: 12px; }
`;
const BadgeCell = styled.div`
  height: 56px;
  padding: 8px 12px;
  border-radius: 12px;
  background: #fff;
  display: flex; align-items: center; justify-content: center;
  @media (max-width: 768px) { height: 40px; }
`;
const BadgeImg = styled.img`
  max-width: 100%; max-height: 100%;
  object-fit: contain; display: block;
  transform: scale(var(--scale, 1));
  transform-origin: center;
`;

const ProductShowcaseSection = ({
  subtitle, title, description, images = [], videoSrc, badgeImages = [],
}) => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    lazyLoad: 'progressive',
    waitForAnimate: false,
    adaptiveHeight: false,
  };

  useEffect(() => {
    if (videoSrc || !images?.length) return;

    const created = images.slice(0, 2).map((href) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      link.setAttribute('data-preload', 'product-showcase');
      document.head.appendChild(link);
      return link;
    });

    return () => {
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, [images, videoSrc]);

  return (
    <Section>
      <TextBox>
        <h2>{subtitle}</h2>
        <h1>{title}</h1>
        <p>{description}</p>

        {badgeImages.length > 0 && (
          <BadgeWrapper>
            {badgeImages.map((b, idx) => {
              const item = typeof b === 'string' ? { src: b } : b || {};
              const { src, alt, scale = 1 } = item;
              return (
                <BadgeCell key={idx}>
                  <BadgeImg
                    src={src}
                    alt={alt || `인증 로고 ${idx + 1}`}
                    style={{ '--scale': scale }}
                    loading="lazy"
                    decoding="async"
                  />
                </BadgeCell>
              );
            })}
          </BadgeWrapper>
        )}
      </TextBox>

      <SliderBox>
        {videoSrc ? (
          <Video controls autoPlay muted loop playsInline>
            <source src={videoSrc} type="video/mp4" />
            브라우저가 video 태그를 지원하지 않습니다.
          </Video>
        ) : (
          <Slider {...settings}>
            {images.map((src, idx) => (
              <SlideFrame key={idx}>
                <img
                  src={src}
                  alt={`제품 이미지 ${idx + 1}`}
                  width="1100"
                  height="840"
                />
              </SlideFrame>
            ))}
          </Slider>
        )}
      </SliderBox>
    </Section>
  );
};

export default ProductShowcaseSection;
